import { partition } from 'lodash';
import { DateTime } from 'luxon';
import { SubmitOfferFormData } from 'src/components/offer/SectionOffer';
import { fromJsonArray } from 'src/core/utils';
import { Address, BasicAddress } from 'src/entities/basic-types/Address';
import CalculatedVolumeDiscount from 'src/entities/cart/response/CalculatedVolumeDiscount';
import OrderProduct from 'src/entities/cart/response/OrderProduct';
import OrderProductListGrouped from 'src/entities/cart/response/OrderProductListGrouped';
import CustomerPaymentMethod from 'src/entities/customer/CustomerPaymentMethod';
import { ProductGroupSlug } from 'src/entities/product-group/ProductGroup';
import Product from 'src/entities/products/Product';
import VolumeDiscount from 'src/entities/volume-discount/VolumeDiscount';

export interface InstallmentPlan {
    rates: Rate[];
    totalAmount: number;
    currencyIso: string;
}

interface Rate {
    date: DateTime;
    localizedDescription: string;
    grossPrice: number;
    nettPrice: number;
    vat: number;
    currencyIso: string;
}

interface ExtraField {
    localizedLabel: string;
    name: string;
    type: 'check' | 'select';
    // Check presents text in modal
    localizedSubject?: string;
    subjectResource?: string;
    // Select
    options?: Option[];
}

interface Option {
    value: string;
    localizedLabel: string;
}
function sortPaymentMethods(a: CustomerPaymentMethod, b: CustomerPaymentMethod): number {
    const nameA = a.normalizedName.toUpperCase();
    const nameB = b.normalizedName.toUpperCase();
    if (nameA < nameB) {
        return -1;
    }
    if (nameA > nameB) {
        return 1;
    }
    return 0;
}

export default class ShoppingCart {
    cartAmount: number;
    totalDiscount: number;
    deliveryAmount: number;
    totalAmount: number;
    totalTaxAmount: number;
    paymentMethodIdentifier?: string;
    currencyIso: string;
    gross: boolean;
    sendMailToCustomer?: boolean;
    sourceDepot?: string;
    orderProductListGrouped: OrderProductListGrouped[];
    containsOrderDirectlyProduct: boolean;
    possiblePaymentMethods: CustomerPaymentMethod[];
    paritalPaymentMethods: CustomerPaymentMethod[];
    paymentMethods: CustomerPaymentMethod[];
    installmentPlan?: InstallmentPlan;
    extraFields: ExtraField[];
    usedCredit: number;
    creditMinimumCartValue: number;
    fromLandingPage: boolean;
    fromLandingPageId?: string;
    fromLandingPageRebateType?: string;
    newCustomer: boolean;
    useCredit: boolean;
    publicId?: string;
    customerId?: number;
    originUserId?: number;
    possibleRebate: number;
    billingAddress?: Address | BasicAddress;
    deliveryAddress?: Address | BasicAddress;
    selectedSourceDepot?: string;
    selectedSendMailToCustomer?: boolean;
    skipDefaultVendorOverride?: boolean;

    constructor(json: any) {
        this.cartAmount = json.cartAmount;
        this.totalDiscount = json.totalDiscount;
        this.deliveryAmount = json.deliveryAmount;
        this.totalAmount = json.totalAmount;
        this.totalTaxAmount = json.totalTaxAmount;
        this.paymentMethodIdentifier = json.paymentMethodIdentifier;
        this.currencyIso = json.currencyIso4217 || json.currencyIso;
        this.gross = json.gross;
        this.sendMailToCustomer = json.sendMailToCustomer ?? false;
        this.sourceDepot = json.sourceDepot;
        this.orderProductListGrouped = fromJsonArray(OrderProductListGrouped, json.orderProductListGrouped).filter(
            orderProductListGroup => orderProductListGroup.products.length > 0
        );
        this.containsOrderDirectlyProduct = json.containsOrderDirectlyProduct ?? false;
        this.extraFields = (json.extraFields ?? []).filter(
            extraField => extraField.type === 'check' || extraField.type === 'select'
        );

        this.possiblePaymentMethods = fromJsonArray(CustomerPaymentMethod, json.possiblePaymentMethods);
        const [paritalPaymentMethods, paymentMethods] = partition(this.possiblePaymentMethods, p => p.isPartial());
        this.paritalPaymentMethods = paritalPaymentMethods.sort(sortPaymentMethods);
        this.paymentMethods = paymentMethods.sort(sortPaymentMethods);

        if (json.installmentPlan) {
            this.installmentPlan = {
                rates: json.installmentPlan.rates.map(rate => ({
                    ...rate,
                    date: DateTime.fromISO(rate.date),
                    currencyIso: rate.currencyIso || 'EUR',
                })),
                totalAmount: json.installmentPlan.totalAmount,
                currencyIso: json.installmentPlan.currencyIso || 'EUR',
            };
        }
        this.usedCredit = json.usedCredit ?? 0;
        this.creditMinimumCartValue = json.creditMinimumCartValue ?? 0;
        this.fromLandingPage = json.fromLandingPage ?? false;
        this.fromLandingPageId = json.fromLandingPageId;
        this.fromLandingPageRebateType = json.fromLandingPageRebateType ?? 'none';
        this.newCustomer = json.newCustomer ?? false;
        this.useCredit = json.useCredit;
        this.publicId = json.publicId;
        this.possibleRebate = json.possibleRebate ?? 0;
        this.customerId = json.customerId;
        this.originUserId = json.originUserId;

        if (json.billingAddress) {
            this.billingAddress = json.billingAddress.id
                ? new Address(json.billingAddress)
                : new BasicAddress(json.billingAddress);
        }
        if (json.deliveryAddress) {
            this.deliveryAddress = json.deliveryAddress.id
                ? new Address(json.deliveryAddress)
                : new BasicAddress(json.deliveryAddress);
        }
        this.selectedSourceDepot = json.selectedSourceDepot;
        this.selectedSendMailToCustomer = json.selectedSendMailToCustomer;
        this.skipDefaultVendorOverride = json.skipDefaultVendorOverride;
    }

    public get isOffer(): boolean {
        return this.publicId != null && this.customerId === this.originUserId;
    }

    public get itemCount(): number {
        return this.getFlatProductList().length;
        // return this.getFlatProductList().reduce((acc, cur) => (acc += cur.quantity), 0);
    }

    public getFlatProductList(): OrderProduct[] {
        return this.orderProductListGrouped.flatMap(group => group.products);
    }

    /**
     * Returns an array of calculated discounts within the shopping cart
     * for products supported in volume discounts
     */
    public getCalculatedVolumeDiscounts(volumeDiscounts: VolumeDiscount[]): CalculatedVolumeDiscount[] {
        return this.orderProductListGrouped.reduce(
            (result: CalculatedVolumeDiscount[], productGroup: OrderProductListGrouped) => {
                const calculatedVolumeDiscount = productGroup.getCalculatedVolumeDiscount(volumeDiscounts);
                if (calculatedVolumeDiscount) result.push(calculatedVolumeDiscount);
                return result;
            },
            []
        );
    }

    public getQuantityOfArticleGroup(articleGroupNormalizedName: string, targetUnit?: string): number {
        return this.orderProductListGrouped
            .filter(group => group.normalizedName === articleGroupNormalizedName)
            .reduce((acc, cur) => (acc += cur.getTotalQuantity(targetUnit)), 0);
    }

    public isEmpty(): boolean {
        return this.orderProductListGrouped.length <= 0;
    }

    public containsUnfinishedProductCustomFields(): boolean {
        return this.orderProductListGrouped.some(group => group.containsUnfinishedProductCustomFields());
    }

    public static emptyCart(): ShoppingCart {
        return new ShoppingCart({
            cartAmount: 0,
            totalDiscount: 0,
            deliveryAmount: 0,
            totalAmount: 0,
            gross: false,
        });
    }

    public hasInstallmentPlan(): boolean {
        return this.installmentPlan !== undefined;
    }

    public hasTabsWithoutMachine(): boolean {
        const hasTabs = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1111');
        const hasMachine = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1211');
        return hasTabs && !hasMachine;
    }

    public hasMachineWithoutTabs(): boolean {
        const hasTabs = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1111');
        const hasMachine = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1211');
        return hasMachine && !hasTabs;
    }

    public getMissingProductDependency(): ProductGroupSlug | undefined {
        const hasTabs = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1111');
        const hasMachine = this.orderProductListGrouped.some(group => group.normalizedName === 'ARTICLE_GROUP_1211');

        if (hasTabs && !hasMachine) return 'machines';
        if (hasMachine && !hasTabs) return 'coffeeTimeTabs';
    }

    public containsVariationsOfProduct(product: Product): boolean {
        const variants = product.variants.map(v => v.articleNumber);
        return this.getFlatProductList().some(p => variants.includes(p.ffProductNumber));
    }

    static offerFormData(cart?: ShoppingCart): SubmitOfferFormData {
        return {
            customerId: cart?.customerId ?? ('' as any),
            deliveryAddress: cart?.deliveryAddress,
            billingAddress: cart?.billingAddress,
            publicId: cart?.publicId,
            selectedSourceDepot: (cart?.selectedSourceDepot as any) ?? 'Lager',
            extra: {},
            additionalProperties: {
                officeProfiRebate: 0,
            },
        };
    }
}
